import React, { useEffect } from 'react'
import { Controller, FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import {
  ControlledHiddenInput,
  ControlledPhoneInput,
  ControlledSwitch,
} from 'transfix-form/components'
import ControlledRadioGroupInput from 'transfix-form/components/ControlledRadioGroupInput'
import Switch, { ISwitchProps } from 'transfix-ui/components/Switch/Switch'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { NewDriverAutosuggest } from 'apps/fleet-planner/components/atoms/NewDriverAutosuggest'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { TSmsPromptSettingsModalSchema } from './SmsPromptSettingsModalSchema'
import { IDriverFragment } from './graphql/driverFragment'
import { useSmsPromptSettingsModalFormContext } from './useSmsPromptSettingsModalForm'

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'grid',
    gap: spacing(2),
    marginTop: spacing(2),
  },
  switch: {
    marginLeft: 11,
  },
  radio: {
    marginLeft: 1,
    marginTop: spacing(1),
  },
  alert: {
    margin: spacing(1),
    marginLeft: -11,
  },
}))

const getErrorMessage = <T extends FieldErrors<TSmsPromptSettingsModalSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

interface ISmsPromptSettingsModalFormProps {
  selectedDriver: IDriverFragment | undefined
}

export const SmsPromptSettingsModalForm = ({
  selectedDriver,
}: ISmsPromptSettingsModalFormProps) => {
  const classes = useStyles()
  const { userMotorCarriers = [] } = useMembershipContext()
  const { control, errors, setValue, watch } = useSmsPromptSettingsModalFormContext()

  const isUpdatesEnabled = watch('enableUpdates')
  const isUpdatesEnabledForAllLoads = watch('enableUpdatesForAllLoads')
  const driver = { ...selectedDriver, motorCarrierId: Number(selectedDriver?.motorCarrierId) }
  const { cellPhone, motorCarrierId = '' } = selectedDriver || {}
  const isAdmin = userMotorCarriers.some(
    mc => mc.motorCarrier.id === Number(motorCarrierId) && mc.membership.role === 'admin'
  )

  useEffect(() => {
    setValue('cellPhone', cellPhone)
  }, [selectedDriver])

  return (
    <form className={classes.root} data-test='SmsPromptSettingsModalForm'>
      <NewDriverAutosuggest disabled value={driver} large onChange={() => {}} />
      <ControlledHiddenInput name='driverId' control={control} value={selectedDriver?.id} />
      <ControlledPhoneInput
        name='cellPhone'
        control={control}
        PhoneInputProps={{
          label: 'Driver Mobile Phone',
          required: true,
          large: true,
          validationMessage: getErrorMessage(errors, 'cellPhone'),
          invalid: !!getErrorMessage(errors, 'cellPhone'),
          placeholder: '(    )     - ',
          dataTest: 'sms-prompt-settings-modal-cell-phone',
        }}
      />
      <div className={classes.switch}>
        {isAdmin ? (
          <>
            <ControlledSwitch
              name='enableUpdates'
              defaultValue={false}
              control={control}
              SwitchProps={{
                label: 'Enable driver status updates',
                dataTest: 'input-enableUpdates',
              }}
            />
            <div className={classes.radio}>
              <ControlledRadioGroupInput
                name='enableUpdatesForAllLoads'
                control={control}
                defaultValue
                RadioProps={{
                  dataTest: 'input-enableUpdatesForAllLoads',
                }}
                options={[
                  {
                    label: 'For all loads',
                    disabled: !isUpdatesEnabled,
                    id: true,
                  },
                  {
                    label: 'For this load only',
                    disabled: !isUpdatesEnabled,
                    id: false,
                  },
                ]}
              />
            </div>
          </>
        ) : (
          <>
            {isUpdatesEnabledForAllLoads && (
              <InlineMessage
                type={INLINE_MESSAGE_VARIANTS.INFO}
                className={classes.alert}
                message='Driver status updates are already enabled for all loads'
              />
            )}
            <ControlledHiddenInput
              name='enableUpdatesForAllLoads'
              control={control}
              value={false}
            />
            <Controller
              name='enableUpdates'
              control={control}
              render={({ onBlur, onChange, value: checked }) => {
                const changeHandler: ISwitchProps['onChange'] = e => {
                  onChange(e.target.checked)
                }
                return (
                  <Switch
                    disabled={isUpdatesEnabledForAllLoads}
                    onChange={changeHandler}
                    checked={!isUpdatesEnabledForAllLoads && checked}
                    onBlur={onBlur}
                    label='Enable driver status updates for this load only'
                  />
                )
              }}
            />
          </>
        )}
      </div>
    </form>
  )
}
